import { ShippingMethodTypes } from "@components/commerce/checkout/types";
import { activeSiteConfig } from "@config/site/site-config";
import type { CountryCode } from "@config/site/types";
import type { Cart } from "@graphql/generated/types";
import { executeCommerceToolsWithRetry } from "@lib/utils/executeCommerceToolsWithRetry";
import { getDeliveryMethodKey } from "@lib/utils/findDeliveryMethods";
import {
    useAddProductToCart,
    useCreateMyCart,
    useRecalculateCart,
    useRemoveProductFromCart,
    useUpdateProductQtyInCart,
} from "@store/hooks";
import { useStore } from "@store/store";
import { isEmpty } from "lodash-es";
import { useLocale } from "next-intl";

export const useCart = () => {
    const executeAddProductToCart = useAddProductToCart();
    const executeUpdateQtyInCart = useUpdateProductQtyInCart();
    const executeCreateMyCart = useCreateMyCart();
    const removeProductFromCart = useRemoveProductFromCart();
    const executeRecalculateCart = useRecalculateCart();

    const getCart = useStore((state) => state.getCart);
    const storeData = useStore((state) => state.data.storeData);
    const locale = useLocale();

    const createCart = async (currency: string, country: CountryCode): Promise<Cart | null> => {
        const CCKey = getDeliveryMethodKey(ShippingMethodTypes.CLICK_AND_COLLECT);
        return await executeCreateMyCart({
            locale,
            storeKey: process.env.NEXT_PUBLIC_STORE_KEY,
            currency,
            country,
            deliveryMethodId: !isEmpty(storeData) ? CCKey : undefined,
        });
    };

    const addToCart = async (sku: string) => {
        if (!sku) {
            return { errors: [{ message: "No SKU provided." }] };
        }

        let activeCart = getCart();
        if (!activeCart) {
            const { currency, country } = activeSiteConfig;
            activeCart = await createCart(currency, country);
        }

        const foundItem = activeCart?.lineItems.find((x) => x.variant?.sku === sku);
        if (foundItem) {
            const { errors } = await executeCommerceToolsWithRetry(
                executeUpdateQtyInCart,
                {
                    lineItemId: foundItem.id,
                    quantity: foundItem.quantity + 1,
                    cartId: activeCart.id,
                    cartVersion: activeCart.version,
                    storeKey: activeCart.store.key,
                    locale: locale,
                },
                0,
                "Executing Update Product Quantity in Cart"
            );
            return { errors };
        } else {
            const { errors } = await executeCommerceToolsWithRetry(
                executeAddProductToCart,
                {
                    cartId: activeCart.id,
                    cartVersion: activeCart.version,
                    storeKey: process.env.NEXT_PUBLIC_STORE_KEY,
                    sku: sku,
                    quantity: 1,
                    distributionChannel: process.env.NEXT_PUBLIC_STORE_KEY,
                    ...(!isEmpty(storeData) && {
                        supplyChannel: { typeId: "channel", id: storeData.supplyChannel?.[0]?.id },
                    }),
                    locale,
                },
                0,
                "Executing Add Product to Cart"
            );

            return { errors };
        }
    };

    const removeFromCart = async (lineItemId: string) => {
        if (!lineItemId) {
            return { errors: [{ message: "No lineItemId provided." }] };
        }
        const activeCart = getCart();
        if (!activeCart) {
            return { errors: [{ message: "No active cart found." }] };
        }

        const item = activeCart.lineItems.find((x) => x.id === lineItemId);
        if (!item) {
            return { errors: [{ message: "Item not found in cart." }] };
        }
        const { errors } = await removeProductFromCart({
            lineItemId: item.id,
            quantity: item.quantity,
            cartId: activeCart.id,
            cartVersion: activeCart.version,
            storeKey: activeCart.store.key,
            locale,
        });

        return {
            errors,
            data: {
                item: item,
                cart: activeCart,
            },
        };
    };

    const recalculateCart = async () => {
        const cart = getCart();
        if (!cart) {
            return { errors: [{ message: "No active cart found." }] };
        }
        if (isEmpty(cart?.lineItems)) return { errors: [{ message: "No items in cart." }] };

        const { errors } = await executeCommerceToolsWithRetry(
            executeRecalculateCart,
            {
                cartId: cart.id,
                cartVersion: cart.version,
                storeKey: cart.store.key,
                locale,
            },
            0,
            "Executing cart recalculation"
        );

        return {
            errors,
        };
    };

    return {
        addToCart,
        removeFromCart,
        recalculateCart,
    };
};
